import React from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
  </svg>
);

const EmptyCircle = () => (
  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
);

const SetupChecklist = ({ studio, courseCount, studentCount, onDismiss }) => {
  const steps = [
    {
      label: 'Create your studio',
      done: true,
      action: null
    },
    {
      label: 'Connect Stripe',
      done: studio?.stripeConnectStatus === 'active',
      action: !studio?.stripeConnectStatus || studio.stripeConnectStatus !== 'active'
        ? { to: '/dashboard/settings?tab=integrations', text: 'Connect' }
        : { text: 'Connected', disabled: true }
    },
    {
      label: 'Create your first offering',
      done: courseCount > 0,
      action: courseCount === 0
        ? { to: '/dashboard/courses?create=true', text: 'Create Offering' }
        : { text: `${courseCount} created`, disabled: true }
    },
    {
      label: 'Get your first student',
      done: studentCount > 0,
      action: studentCount === 0
        ? (courseCount > 0
          ? { to: '/dashboard/courses', text: 'Share Link' }
          : { to: '/dashboard/courses?create=true', text: 'Create Offering First' })
        : { text: `${studentCount} enrolled`, disabled: true }
    }
  ];

  const doneCount = steps.filter(s => s.done).length;
  const allDone = doneCount === steps.length;
  const progress = Math.round((doneCount / steps.length) * 100);

  const handleDismiss = async () => {
    try {
      await api.patch('/studio/onboarding', { setupChecklistDismissed: true });
    } catch (e) {
      console.error('Failed to dismiss checklist:', e.message);
    }
    onDismiss();
  };

  if (allDone) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Get your studio ready</h3>
        <span className="text-xs text-gray-500">{doneCount}/{steps.length} done</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
        <div
          className="bg-brand-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {step.done ? <CheckIcon /> : <EmptyCircle />}
              <span className={`text-sm ${step.done ? 'text-gray-500 line-through' : 'text-gray-900 font-medium'}`}>
                {step.label}
              </span>
            </div>
            {step.action && !step.action.disabled && step.action.to && (
              <Link
                to={step.action.to}
                className="text-xs font-medium text-brand-500 hover:text-brand-600 transition-colors"
              >
                {step.action.text}
              </Link>
            )}
            {step.action?.disabled && (
              <span className="text-xs text-green-600 font-medium">{step.action.text}</span>
            )}
          </div>
        ))}
      </div>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="mt-4 text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        Dismiss
      </button>
    </div>
  );
};

export default SetupChecklist;
